"use client";

import { useEffect, useRef, useState } from "react";
import { Markdown } from "./markdown";
import clsx from "clsx";
import { useTranslations } from "next-intl";

interface ExpandableMarkdownProps {
  content: string;
  className?: string;
}

export function ExpandableMarkdown({
  content,
  className,
}: Readonly<ExpandableMarkdownProps>) {
  const t = useTranslations("ExpandableMarkdown");
  const [isExpanded, setIsExpanded] = useState(false);
  const [isOverflowing, setIsOverflowing] = useState(false);
  const contentRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const measure = () => {
      if (isExpanded) return;

      const el = contentRef.current;
      if (!el) return;

      const overflowing = el.scrollHeight > el.clientHeight + 1;
      setIsOverflowing(overflowing);
    };

    measure();
    window.addEventListener("resize", measure);
    return () => window.removeEventListener("resize", measure);
  }, [content, isExpanded]);

  return (
    <div className={clsx(className)}>
      <div ref={contentRef} className={!isExpanded ? "line-clamp-2" : ""}>
        <Markdown>{content}</Markdown>
      </div>
      {isOverflowing && (
        <div className="mt-1 flex justify-end">
          <button
            onClick={() => setIsExpanded(!isExpanded)}
            className="text-sm font-medium text-purple-600 hover:text-purple-500 dark:text-purple-400 dark:hover:text-purple-300 cursor-pointer"
          >
            {isExpanded ? t("readLess") : t("readMore")}
          </button>
        </div>
      )}
    </div>
  );
}
